import {
  Box, Collapse, TableCell,
  Tooltip, Typography,
} from '@mui/material';
import moment from 'moment';
import React, {
  FC,
} from 'react';
import { NavLink } from 'react-router-dom';
import { faCut } from '@fortawesome/free-solid-svg-icons';
import { ActionStatus, ActionType } from '../../../api/vault/assetStatus/interfaces';
import Button from '../VaultButton';
import {
  JobDetailsContainer, RowActionButtonContainer, StatusContainer,
  StatusIcon, StyledBodyTableRow, StyledClipperButton, StyledDeleteIcon, TabeleCellDateContent, TableCellContent,
} from './JobListTableRowStyles';
import { DeleteIcon } from '../../VirtualClipper/components/Icon';
import { Routes, VAULT_VIDEO_TYPE } from '../../../constants';
import { ILiveStreamJob } from '../../../api/vault/livestream';
import {
  getSecurityProfileName, getStatusOfLiveEvent, getStreamTitle, handleClipper,
} from '../../LiveEvents/LiveEventsList/component';
import { StatusIcon as LiveEventStatusIcon } from '../../LiveEvents/LiveEventsList/styles';
import { labelForStreamStatus, tooltipForStreamStatus } from '../LiveEvents/statusToLabelMap';
import { ISecurityProfile } from '../../../api/vault/securityProfiles';
import { idsToSecurityProfileNames } from '../../../state/securityProfiles';
import { FaIcon } from '../Icons';
import { StreamStatus } from '../../../enums';
import LiveEventMenu from '../../LiveEvents/LiveEventMenu/component';
import { IFormattedAssetStatus, JobTab } from '../../../state/jobStatus';

interface IProps {
  liveEvent: ILiveStreamJob,
  job: IFormattedAssetStatus,
  isExchange: boolean,
  onJobCancel: (jobId: string) => void,
  onRetryAssetJob: (assetid: string, actionType: ActionType, jobId: string, assetName: string) => void,
  isRetryDisabled: boolean,
  key: string,
  securityProfilesMapping: ISecurityProfile[],
  jobTab: JobTab,
  isSuperAdmin: boolean,
}

const cellAligment = 'left';

const JobListTableRow: FC<IProps> = (props: IProps) => {
  const {
    job, isExchange, onJobCancel, onRetryAssetJob, jobTab,
    isRetryDisabled, liveEvent, securityProfilesMapping, isSuperAdmin,
  } = props;

  const isExchangeAndNotExchangeCreate = isExchange && jobTab !== JobTab.ExchangeCreate;

  const [open, setOpen] = React.useState(false);

  const getLiveEventRow = () => (
    <>
      <StyledBodyTableRow className="Job-List-Table-Row" data-job-row={liveEvent._id} onClick={() => { setOpen(!open); }}>
        <TableCell align={cellAligment}>
          <TableCellContent>
            {liveEvent._source.title}
          </TableCellContent>
        </TableCell>
        <TableCell align={cellAligment}>
          <TableCellContent>
            {getStreamTitle(liveEvent)}
          </TableCellContent>
        </TableCell>
        <TableCell align={cellAligment}>
          <TableCellContent>{getSecurityProfileName(liveEvent, securityProfilesMapping)}</TableCellContent>
        </TableCell>
        <TableCell align={cellAligment}>
          <TableCellContent>
            {moment(liveEvent._source.start).format('LLL')}
          </TableCellContent>
        </TableCell>
        <TableCell align={cellAligment}>
          <TableCellContent>
            {liveEvent.duration}
          </TableCellContent>
        </TableCell>
        <TableCell align={cellAligment}>
          <StatusContainer>
            <Tooltip
              title={tooltipForStreamStatus(liveEvent._source.streamStatus, liveEvent._source.streamErrorReason)}
              aria-label="Stream status"
            >
              <LiveEventStatusIcon status={getStatusOfLiveEvent(liveEvent)} />
            </Tooltip>
          </StatusContainer>
        </TableCell>
        <TableCell>
          <NavLink
            target="_blank"
            to={Routes.VIRTUAL_CLIPPER}
            onClick={(e) => {
              e.stopPropagation();
              handleClipper(liveEvent, e.currentTarget);
            }}
          >
            <StyledClipperButton
              id="AssetDetails.OperationArea.NewClip"
              variant="transparentOnWhite"
              data-tip="Clip Editor"
              disabled={![StreamStatus.Complete, StreamStatus.Live].includes(liveEvent._source.streamStatus)}
              data-for="operations-tooltip"
            >
              <FaIcon icon={faCut} />
            </StyledClipperButton>
          </NavLink>
        </TableCell>
        <TableCell>
          <LiveEventMenu asset={liveEvent} inJobsTab />
        </TableCell>
      </StyledBodyTableRow>
      <Collapse in={open} timeout="auto" unmountOnExit style={{ width: 'max-content' }}>
        <Box sx={{ margin: 1, marginLeft: 4 }}>
          <Typography variant="h6" gutterBottom component="div">
            Job Details
          </Typography>
          <JobDetailsContainer>
            <p><span>Status:</span><LiveEventStatusIcon status={getStatusOfLiveEvent(liveEvent)} />{labelForStreamStatus(liveEvent._source.streamStatus)}</p>
            {liveEvent._source.streamStatus === StreamStatus.Error && liveEvent._source.streamErrorReason
              && (<p><span>Error reason:</span>{liveEvent._source.streamErrorReason}</p>)}
            <p><span>Event Title:</span>{liveEvent._source.title}</p>
            {liveEvent._source.streamChannel && (<p><span>Stream Channel:</span>{liveEvent._source.streamChannel}</p>)}
            <p><span>Stream Url:</span>{liveEvent._source.streamUrl}</p>
            <p><span>Security Profile:</span>{idsToSecurityProfileNames(liveEvent._source.securityProfiles, securityProfilesMapping)}</p>
            <p><span>Start (EDT):</span>{moment(liveEvent._source.start).format('LLL')}</p>
            <p><span>Duration (mins):</span>{liveEvent.duration}</p>
            {isSuperAdmin && <p><span>Asset Id:</span> {liveEvent._id}</p>}
          </JobDetailsContainer>
        </Box>
      </Collapse>
    </>
  );

  const getJobRow = () => {
    const displayRetry = job.videoType === VAULT_VIDEO_TYPE.Exchange && job.actionStatus === ActionStatus.Failed && (job.actionType === ActionType.Create
      || job.actionType === ActionType.Download || job.actionType === ActionType.PushToCloud);
    const actionButtons: React.ReactElement[] = [];
    actionButtons.push(
      <StyledDeleteIcon key={'delete'} onClick={(event) => { onJobCancel(job._id); event.stopPropagation(); }} size="large">
        <DeleteIcon onClickHandler={undefined} disabled={false} />
      </StyledDeleteIcon>,
    );
    if (displayRetry) {
      actionButtons.push(
        <Button
          key={'retry'}
          variant="primaryOnWhite"
          onClick={(event) => { onRetryAssetJob(job.assetId, job.actionType, job._id, job.assetName); event.stopPropagation(); }}
          disabled={isRetryDisabled}
        >
          Retry
        </Button>,
      );
    }
    const showMachine = [JobTab.Upload, JobTab.Download].includes(jobTab);

    const displayExchangeSport = jobTab === JobTab.ExchangeCreate;
    const exchangeSport = job.metadata ? job.metadata.find((val) => val.label === 'exchangeSport')?.value : '';
    return (
      <>
        <StyledBodyTableRow className="Job-List-Table-Row" data-job-row={job.assetId} onClick={() => { setOpen(!open); }}>
          <TableCell align={cellAligment}>
            <StatusContainer>
              {/* TODO: the tooltip has currently been removed for VA-2595, a non material ui one would need to be implemented */}
              <StatusIcon status={job.actionStatus} />
              {job.actionStatusText}
            </StatusContainer>
          </TableCell>
          <TableCell align={cellAligment} className="actionType">
            {job.actionTypeText}
          </TableCell>
          <TableCell align={cellAligment}>
            <TableCellContent>
              {job.user}
            </TableCellContent>
          </TableCell>
          <TableCell align={cellAligment}>
            <TableCellContent>
              {job.assetName}
            </TableCellContent>
          </TableCell>
          {displayExchangeSport && (
            <TableCell align={cellAligment}>
              <TableCellContent>
                {exchangeSport || 'N/A'}
              </TableCellContent>
            </TableCell>
          )}
          {isExchangeAndNotExchangeCreate && (
            <TableCell align={cellAligment}>
              <TableCellContent>
                {job.exportFileName || 'N/A'}
              </TableCellContent>
            </TableCell>
          )}
          <TableCell align={cellAligment}>
            <TabeleCellDateContent>
              <p>{job.date}</p>
              <p>{job.time}</p>
            </TabeleCellDateContent>
          </TableCell>
          <TableCell align={cellAligment}>
            <TableCellContent>{job.progress}</TableCellContent>
          </TableCell>
          {showMachine && (
            <TableCell align={cellAligment}>
              <TableCellContent>
                {job.actionMachine}
              </TableCellContent>
            </TableCell>
          )}
          {actionButtons.length > 0 && (
            <TableCell align={cellAligment}>
              <RowActionButtonContainer>
                {actionButtons}
              </RowActionButtonContainer>
            </TableCell>
          )}
        </StyledBodyTableRow>
        <Collapse in={open} timeout="auto" unmountOnExit style={{ width: 'max-content' }}>
          <Box sx={{ margin: 1, marginLeft: 4 }}>
            <Typography variant="h6" gutterBottom component="div">
              Job Details
            </Typography>
            <JobDetailsContainer>
              <p><span>Status:</span> {job.actionStatusText}</p>
              <p><span>Action:</span> {job.actionTypeText}</p>
              <p><span>User:</span> {job.user}</p>
              <p><span>Asset Title:</span> {job.assetName}</p>
              {isExchangeAndNotExchangeCreate && (<p><span>Export File Name:</span> {job.exportFileName || 'N/A'}</p>)}
              <p><span>Time:</span> {job.localizedActionDate}</p>
              <p><span>Progress:</span> {job.progress}</p>
              {showMachine && (<p><span>Machine:</span> {job.actionMachine}</p>)}
              {isSuperAdmin && <p><span>Asset Id:</span> {job.assetId}</p>}
            </JobDetailsContainer>
          </Box>
        </Collapse>
      </>
    );
  };

  if (liveEvent !== null) return getLiveEventRow();
  return getJobRow();
};

export default JobListTableRow;
